import { supabase } from '@/integrations/supabase/client';
import { dataService } from './dataService';
import { startupTaskGenerator } from './startupTaskGenerator';

export interface DailyTask {
  id: string;
  user_id: string;
  title: string;
  description: string;
  task_type: 'social_media' | 'journal_entry';
  completed: boolean;
  due_date: string;
  created_at: string;
  updated_at: string;
  priority: string;
  category: string;
}

class DailyTaskService {
  private readonly SOCIAL_TASK_TITLE = 'Post on Social Media';
  private readonly JOURNAL_TASK_TITLE = 'Enter Journal Details';

  /**
   * Creates default daily tasks for a user if they don't exist for today
   */
  async createDailyTasksIfNeeded(userId: string): Promise<void> {
    if (!userId) return;

    const today = new Date().toISOString().split('T')[0];

    try {
      // Check if daily tasks already exist for today with both title and task_type check
      const { data: existingTasks, error: checkError } = await supabase
        .from('tasks')
        .select('id, title, task_type')
        .eq('user_id', userId)
        .eq('due_date', today)
        .or(`title.eq.${this.SOCIAL_TASK_TITLE},title.eq.${this.JOURNAL_TASK_TITLE}`)
        .or('task_type.eq.social_media,task_type.eq.journal_entry');

      if (checkError) {
        console.error('Error checking existing daily tasks:', checkError);
        return;
      }

      // More robust duplicate checking
      const hasSocialTask = existingTasks?.some(task => 
        task.title === this.SOCIAL_TASK_TITLE || task.task_type === 'social_media'
      );
      const hasJournalTask = existingTasks?.some(task => 
        task.title === this.JOURNAL_TASK_TITLE || task.task_type === 'journal_entry'
      );

      const tasksToCreate = [];

      if (!hasSocialTask) {
        tasksToCreate.push({
          user_id: userId,
          title: this.SOCIAL_TASK_TITLE,
          description: 'Log your social post in Social Tracker tab to build your online presence',
          due_date: today,
          completed: false,
          priority: 'high',
          category: 'daily_routine',
          task_type: 'social_media'
        });
      }

      if (!hasJournalTask) {
        tasksToCreate.push({
          user_id: userId,
          title: this.JOURNAL_TASK_TITLE,
          description: 'Document your founder journey and insights in your Journal',
          due_date: today,
          completed: false,
          priority: 'high',
          category: 'daily_routine',
          task_type: 'journal_entry'
        });
      }

      if (tasksToCreate.length > 0) {
        const { error: insertError } = await supabase
          .from('tasks')
          .insert(tasksToCreate);

        if (insertError) {
          console.error('Error creating daily tasks:', insertError);
        } else {
          console.log(`Created ${tasksToCreate.length} daily tasks for user`);
        }
      }

      // Check if user has validated their idea and generate startup tasks if needed
      const { data: profile } = await supabase
        .from('profiles')
        .select('idea_validated')
        .eq('user_id', userId)
        .single();

      if (profile?.idea_validated) {
        const hasStartupTasks = await startupTaskGenerator.hasStartupTasks(userId);
        if (!hasStartupTasks) {
          console.log('Generating startup methodology tasks for validated user...');
          await startupTaskGenerator.generateStartupTasks(userId);
        }
      }
    } catch (error) {
      console.error('Error in createDailyTasksIfNeeded:', error);
    }
  }

  /**
   * Marks a daily task as completed based on task type
   */
  async markDailyTaskCompleted(userId: string, taskType: 'social_media' | 'journal_entry'): Promise<void> {
    if (!userId || !taskType) return;

    const today = new Date().toISOString().split('T')[0];
    const taskTitle = taskType === 'social_media' ? this.SOCIAL_TASK_TITLE : this.JOURNAL_TASK_TITLE;

    try {
      const { error } = await supabase
        .from('tasks')
        .update({ 
          completed: true,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('title', taskTitle)
        .eq('due_date', today)
        .eq('completed', false); // Only update if not already completed

      if (error) {
        console.error(`Error marking ${taskType} task as completed:`, error);
      } else {
        console.log(`Marked ${taskType} daily task as completed`);
        
        // Create success notification
        await dataService.createNotification(
          userId,
          `✅ Daily task completed: ${taskTitle}`,
          'success'
        );
      }
    } catch (error) {
      console.error(`Error in markDailyTaskCompleted for ${taskType}:`, error);
    }
  }

  /**
   * Gets today's daily tasks for a user
   */
  async getTodaysDailyTasks(userId: string): Promise<DailyTask[]> {
    if (!userId) return [];

    const today = new Date().toISOString().split('T')[0];

    try {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', userId)
        .eq('due_date', today)
        .in('title', [this.SOCIAL_TASK_TITLE, this.JOURNAL_TASK_TITLE])
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching daily tasks:', error);
        return [];
      }

      return (data || []).map(task => ({
        ...task,
        task_type: task.title === this.SOCIAL_TASK_TITLE ? 'social_media' : 'journal_entry'
      })) as DailyTask[];
    } catch (error) {
      console.error('Error in getTodaysDailyTasks:', error);
      return [];
    }
  }

  /**
   * Creates notifications for pending daily tasks
   */
  async createPendingTaskNotifications(userId: string): Promise<void> {
    if (!userId) return;

    try {
      const dailyTasks = await this.getTodaysDailyTasks(userId);
      const pendingTasks = dailyTasks.filter(task => !task.completed);

      if (pendingTasks.length === 0) return;

      for (const task of pendingTasks) {
        const message = task.task_type === 'social_media' 
          ? "Don't forget to post on social media today! 📱"
          : "Don't forget your daily journal entry! 📝";

        await dataService.createNotification(
          userId,
          message,
          'reminder',
          { task_type: task.task_type, task_id: task.id }
        );
      }
    } catch (error) {
      console.error('Error creating pending task notifications:', error);
    }
  }
}

export const dailyTaskService = new DailyTaskService();